import React, { useState } from 'react';
import {
  Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, TextField,
} from '@mui/material';
import { blockService } from '../services/blockService';
import { reportService, ReportReason, ReportReasonValue, REPORT_REASON_LABELS } from '../services/reportService';
import { useNotification } from './NotificationContext';

export type ReportTarget =
  | { type: 'Post' }
  | { type: 'Comment'; commentId: string }
  | { type: 'Event'; eventId: string };

const TARGET_LABEL: Record<ReportTarget['type'], string> = { Post: 'post', Comment: 'comment', Event: 'event' };

// Shared by reporting a post, a comment or a region event — the caller passes which via the
// target prop, and this dialog only needs a reason (required) plus optional free-text detail.
const ReportDialog: React.FC<{
  target: ReportTarget;
  /** The post being reported, or the post a reported comment belongs to. Not used for events. */
  postId?: string;
  /** Who wrote the reported content; when set, the dialog offers to block them in the same step. */
  offender: { userId: string; displayName: string } | null;
  onClose: () => void;
  onBlocked: (userId: string) => void;
}> = ({ target, postId, offender, onClose, onBlocked }) => {
  const { showSuccess, showError } = useNotification();
  const [reason, setReason] = useState<ReportReasonValue>(ReportReason.Spam);
  const [details, setDetails] = useState('');
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await reportService.createReport({
        targetType: target.type,
        postId: postId ?? '',
        commentId: target.type === 'Comment' ? target.commentId : undefined,
        eventId: target.type === 'Event' ? target.eventId : undefined,
        reason,
        details: details.trim() || undefined,
      });
      showSuccess('Report submitted — thanks for flagging this');
      if (alsoBlock && offender) {
        // The report is already filed; a failed block shouldn't read as a failed report.
        try {
          await blockService.block(offender.userId);
          showSuccess(`Blocked ${offender.displayName}`);
          onBlocked(offender.userId);
        } catch {
          showError(`Report sent, but couldn't block ${offender.displayName} — try again from their profile`);
        }
      }
      onClose();
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Report {TARGET_LABEL[target.type]}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="report-reason-label">Reason</InputLabel>
            <Select
              labelId="report-reason-label"
              label="Reason"
              value={reason}
              onChange={e => setReason(e.target.value as ReportReasonValue)}
            >
              {Object.values(ReportReason).map(r => (
                <MenuItem key={r} value={r}>{REPORT_REASON_LABELS[r]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Additional details (optional)"
            value={details}
            onChange={e => setDetails(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          {offender && (
            <FormControlLabel
              control={<Checkbox checked={alsoBlock} onChange={e => setAlsoBlock(e.target.checked)} />}
              label={`Also block ${offender.displayName} — you won't see each other's posts or comments`}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Submit report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportDialog;
